import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

export const resendOTP = createAsyncThunk(
  "delivery_user/resendOTP",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("delivery-partner/resend-otp", data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to resend OTP.");
    }
  }
);

export const registerDelivaryPartaner = createAsyncThunk(
  "delivery_user/registerDelivaryPartaner",
  async (data, { rejectWithValue }) => {
    try {
      let formData = new FormData();
      let files = [
        "residence_image",
        "driving_licence_image",
        "insurance_image",
        "vehicle_certificate_image"
      ];
      Object.entries(data).forEach(([key, value]) => {
        if (files.includes(key) && !(value instanceof File)) {
          formData.append(key, '');
        } else if (key === "other_documents" && Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File) {
              formData.append("other_documents[]", file);
            }
          });
        } else if (value) {
          formData.append(key, value);
        }
      });

      const response = await api.post("delivery-partner/register-request", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to register.");
    }
  }
);

export const completeRegistration = createAsyncThunk(
  "delivery_user/completeRegistration",
  async (data, { rejectWithValue }) => {
    try {
      let formData = new FormData();
      let files = [
        "residence_image",
        "driving_licence_image",
        "insurance_image",
        "vehicle_certificate_image"
      ];
      Object.entries(data).forEach(([key, value]) => {
        if (files.includes(key) && !(value instanceof File)) {
          formData.append(key, '');
        } else if (key === "other_documents" && Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File) {
              formData.append("other_documents[]", file);
            }
          });
        } else if (value) {
          formData.append(key, value);
        }
      });

      const response = await api.post("delivery-partner/register", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to store data.");
    }
  }
);

export const verifyOTP = createAsyncThunk(
  "delivery_user/verifyOTP",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("delivery-partner/verify-otp", data);

      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to verify OTP");
    }
  }
);

// for downloading internal user report
export const downloadDeliveryPartnerReport = createAsyncThunk(
  "delivery_user/downloadDeliveryPartnerReport",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/download-delivery-partner-report");
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to download report.");
    }
  }
);

const initialState = {
  loading: false,
  resend_loading: false,
  verify_loading: false,
};

const deliveryUserSlice = createSlice({
  name: "delivery_user",
  initialState,
  reducers: {
    resetStatus(state) {
      state.status = "idle";
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resendOTP.pending, (state) => {
        state.resend_loading = true;
      })
      .addCase(resendOTP.fulfilled, (state, action) => {
        state.resend_loading = false;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.resend_loading = false;
      })
      .addCase(registerDelivaryPartaner.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerDelivaryPartaner.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(registerDelivaryPartaner.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(verifyOTP.pending, (state) => {
        state.verify_loading = true;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.verify_loading = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.verify_loading = false;
      })
      .addCase(completeRegistration.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(completeRegistration.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(completeRegistration.rejected, (state, action) => {
        state.loading = false;
      })
      // Download delivery Partner Report
      .addCase(downloadDeliveryPartnerReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(downloadDeliveryPartnerReport.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(downloadDeliveryPartnerReport.rejected, (state, action) => {
        state.loading = false;
      })
  }
});

export const { resetStatus } = deliveryUserSlice.actions;
export default deliveryUserSlice.reducer;
